"use client";

import { useEffect } from "react";

type ViewportAnimatorProps = {
  selector?: string;
  once?: boolean;
  threshold?: number;
  rootMargin?: string;
};

/**
 * ViewportAnimator
 * - Pauses animation classes until the element is in view, then re-applies them.
 * - Targets classes by default:
 *   .animate-fade-in-up, .animate-fade-in, .animate-slide-in-left, .animate-slide-in-right
 * - Keeps existing Tailwind/CSS classes in markup. No need to wrap elements.
 * - Respects prefers-reduced-motion (shows elements without animation).
 */
export default function ViewportAnimator({
  selector = ".animate-fade-in-up, .animate-fade-in, .animate-slide-in-left, .animate-slide-in-right",
  once = true,
  threshold = 0.2,
  rootMargin = "0px 0px -10% 0px",
}: ViewportAnimatorProps) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const elements = Array.from(
      document.querySelectorAll(selector)
    ) as HTMLElement[];

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Store animation-related classes per element, then strip them until visible
    const stored = new WeakMap<Element, string[]>();
    elements.forEach((el) => {
      const toStrip = Array.from(el.classList).filter(
        (c) => /^animate-/.test(c) || /^animation-delay-\d+$/.test(c)
      );
      stored.set(el, toStrip);

      // Remove animation-related classes for now
      toStrip.forEach((c) => el.classList.remove(c));

      if (prefersReducedMotion) {
        // Show immediately without animation
        el.classList.remove("opacity-0");
        el.classList.add("opacity-100");
      } else {
        // Hide until intersecting
        el.classList.remove("opacity-100");
        el.classList.add("opacity-0", "will-change-transform");
      }
    });

    if (prefersReducedMotion) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target as HTMLElement;
          const classes = stored.get(el) || [];
          // Re-apply original animation classes
          classes.forEach((c) => el.classList.add(c));
          el.classList.remove("opacity-0", "will-change-transform");

          if (once) {
            observer.unobserve(el);
          }
        });
      },
      { threshold, rootMargin }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [selector, once, threshold, rootMargin]);

  return null;
}
